import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { Section } from "../components/Section";
import {
  ArchivedBadge,
  isTubeArchived,
} from "../components/tubes/ArchivedBadge";
import { TubeDetailForm } from "../components/tubes/TubeDetailForm";
import { useTabSearch } from "../hooks";

export function EditTubePage(params: { id: number }) {
  const tabsProps = useTabSearch();
  const { data, isPending, error } = trpc.admin.tube.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const tube = data;
  const archived = isTubeArchived(tube);

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/tubes" }}>Containers</Breadcrumb>
            <Breadcrumb>{tube.name}</Breadcrumb>
          </>
        }
        actions={
          archived ? (
            <RestoreTubeDialog tube={tube}>
              <Button variant="outline">Restore</Button>
            </RestoreTubeDialog>
          ) : (
            <div className="flex gap-2">
              <ArchiveTubeDialog
                tube={tube}
                preferenceCount={tube._count.testPreferences}
              >
                <Button variant="outline">Archive</Button>
              </ArchiveTubeDialog>
              <DeleteTubeDialog tube={tube}>
                <Button variant="destructive">Delete</Button>
              </DeleteTubeDialog>
            </div>
          )
        }
      >
        <span className="inline-flex items-center gap-2">
          {tube.name}
          {archived ? <ArchivedBadge /> : null}
        </span>
      </Section.Title>
      <Tabs {...tabsProps}>
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
        </TabsList>
        <TabPanel id="details">
          <TubeDetailForm tube={tube} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function ArchiveTubeDialog({
  children,
  tube,
  preferenceCount,
}: {
  children: ReactNode;
  tube: { id: number; name: string };
  preferenceCount: number;
}) {
  const [open, setOpen] = useState(false);
  const archiveTube = trpc.admin.tube.archive.useMutation();

  const onConfirm = () => {
    archiveTube.mutate(
      tube,
      mutationResponse({
        errorTitle: "Error archiving container",
        successMessage: "Container archived",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  const preferenceMessage =
    preferenceCount > 0
      ? ` This container is referenced by ${preferenceCount} ${
          preferenceCount === 1 ? "test's" : "tests'"
        } container preferences. They will keep the preference but won't see it during accessioning.`
      : "";

  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      title="Archive container"
      message={`Archived containers are hidden from accessioning and menus. You can restore later.${preferenceMessage}`}
      onConfirm={onConfirm}
      loading={archiveTube.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}

function RestoreTubeDialog({
  children,
  tube,
}: {
  children: ReactNode;
  tube: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const restoreTube = trpc.admin.tube.restore.useMutation();

  const onConfirm = () => {
    restoreTube.mutate(
      tube,
      mutationResponse({
        errorTitle: "Error restoring container",
        successMessage: "Container restored",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      open={open}
      onOpenChange={setOpen}
      title="Restore container"
      message="Are you sure you want to restore this container? It will appear in accessioning and menus again."
      onConfirm={onConfirm}
      loading={restoreTube.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}

function DeleteTubeDialog({
  children,
  tube,
}: {
  children: ReactNode;
  tube: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteTube = trpc.admin.tube.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deleteTube.mutate(
      tube,
      mutationResponse({
        errorTitle: "Error deleting container",
        successMessage: "Container deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/tubes" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete container"
      message="Are you sure you want to delete this container?"
      onConfirm={onConfirm}
      loading={deleteTube.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
